/* eslint-disable promise/no-promise-in-callback */
import type {
  AxiosRequestConfig,
  AxiosResponse,
  InternalAxiosRequestConfig
} from "axios";
import getClientUniqueId from "helpers/getClientUniqueId";
import ms from "ms";

import ENV from "../constants/ENV";
import HEADERS from "../constants/HEADERS";
import COUNTRY from "../constants/COUNTRY";
import MEMBER_ROLES from "../constants/MEMBER_ROLES";
import store from "../store/configureStore";

import HttpService, {
  HttpServiceError,
  HttpServiceRequestConfig,
  OfflineError
} from "./HttpService";

// --- Types ---

// --- Custom Response Type ---
export interface CustomResponse<T> extends Omit<AxiosResponse, "data"> {
  actualResponseData: T;
  data: T extends { data?: infer D } ? D : T;
  errors?: ApiError[];
}

// --- API error structure from the backend. ---
export interface ApiError {
  code: {
    name: string;
    value: string;
  },
  title: string;
  message: string;
}

// --- Redux Action Types ---

const API_CLIENT_ACTIONS = {
  REQUEST_SENT: "##api/REQUEST_SENT",
  RESPONSE_RECEIVED: "##api/RESPONSE_RECEIVED",
  REQUEST_FAILED: "##api/REQUEST_FAILED",
  ONLINE_STATUS_CHANGED: "##api/ONLINE_STATUS_CHANGED"
} as const;

// --- HTTP Status Codes to treat as "successful" responses ---

const SUCCESS_STATUS_CODES = [ 200, 400, 422 ];

// --- Create and Configure ApiClient Instance ---

/**
 * API client for MyKredit Spain application.
 *
 * This is a pre-configured HttpService instance with:
 * - App-specific base URL and timeout
 * - Default headers (application, language, member role, etc.)
 * - Redux action dispatching for request/response logging
 * - Online/offline status monitoring
 *
 * @example
 * ```typescript
 * import apiClient from "services/ApiClient";
 *
 * // Basic request
 * const response = await apiClient.request<CustomResponse<UserDto>>({
 *   url: "/api/customer/get",
 *   method: "GET"
 * });
 *
 * // With URL variables
 * const loan = await apiClient.request<CustomResponse<LoanDto>>({
 *   url: "/api/loan/{loanId}",
 *   urlVariables: { loanId: 12345 }
 * });
 *
 * // POST request with data
 * const result = await apiClient.request<CustomResponse<CreateResponse>>({
 *   url: "/api/loan/apply",
 *   method: "POST",
 *   data: { amount: 1000, term: 30 }
 * });
 * ```
 */
const apiClient = new HttpService({
  baseURL: ENV.API_ENDPOINT,
  defaultTimeout: ms(ENV.API_TIMEOUT_DURATION as ms.StringValue),
  withCredentials: false,
  debug: false,
  enableOfflineDetection: true,
  enableErrorTransform: true,
  validateStatus: () => true // Accept all status codes for custom handling
});

// --- Setup Request Interceptor ---

apiClient.addRequestInterceptor(
  "redux-request-logger",
  (config: InternalAxiosRequestConfig) => {
    if (!(config as any).ignoreLogs) {
      store.dispatch({
        type: API_CLIENT_ACTIONS.REQUEST_SENT,
        payload: config,
        timestamp: new Date().toISOString()
      });
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// --- Setup Response Interceptor ---

apiClient.addResponseInterceptor(
  "redux-response-logger",
  (response: AxiosResponse) => {
    if (!(response.config as any).ignoreLogs) {
      store.dispatch({
        type: API_CLIENT_ACTIONS.RESPONSE_RECEIVED,
        payload: response,
        timestamp: new Date().toISOString()
      });
    }

    // Transform response: reject non-success status codes
    if (!SUCCESS_STATUS_CODES.includes(response.status)) {
      const correlationId = response.headers?.correlationid as
        | string
        | undefined;
      const clientUniqueId = getClientUniqueId();
      return Promise.reject(
        new Error(
          `${response.config.url}, correlationId: ${correlationId}, clientUniqueId: ${clientUniqueId}`
        )
      );
    }

    return {
      ...response,
      actualResponseData: response.data || null,
      data: response.data ? response.data.data : null,
      errors: response.data ? response.data.errors : null
    };
  },
  (error: unknown) => {
    // Extract error details safely
    const axiosError = error as {
      config?: { url?: string; method?: string; ignoreLogs?: boolean };
      response?: { status?: number };
      code?: string;
      message?: string;
    };

    if (!axiosError.config?.ignoreLogs) {
      store.dispatch({
        type: API_CLIENT_ACTIONS.REQUEST_FAILED,
        payload: {
          url: axiosError.config?.url,
          method: axiosError.config?.method,
          status: axiosError.response?.status,
          code: axiosError.code,
          message: axiosError.message
        },
        timestamp: new Date().toISOString()
      });
    }
    return Promise.reject(error);
  }
);

// --- Setup Online Status Listener ---

apiClient.onOnlineStatusChange((isOnline) => {
  store.dispatch({
    type: API_CLIENT_ACTIONS.ONLINE_STATUS_CHANGED,
    payload: { isOnline },
    timestamp: new Date().toISOString()
  });
});

// --- Setup Default Headers ---

apiClient.setHeader(HEADERS.application, COUNTRY.application);
apiClient.setHeader(HEADERS.appVersion, __BUILD_VERSION__);
apiClient.setHeader(HEADERS.memberRole, MEMBER_ROLES.user);
apiClient.setHeader(HEADERS.clientUnique, getClientUniqueId());
apiClient.setHeader(HEADERS.acceptLanguage, COUNTRY.acceptLanguage);

// --- Export ---

export default apiClient;
export { API_CLIENT_ACTIONS, HttpServiceError, OfflineError };
export type { HttpServiceRequestConfig } from "./HttpService";

// --- Custom Axios Instance for Orval ---
export const customInstance = <T>(
  config: AxiosRequestConfig,
  options?: HttpServiceRequestConfig
): Promise<CustomResponse<T>> => {
  const promise = apiClient
    .request({
      ...config,
      ...options
    })
    .then((response) => response as CustomResponse<T>);
  return promise;
};
